import { AxiosResponse } from 'axios';
import apiClient from 'src/api/axiosClient';

type InFlightEntry<T> = {
  promise: Promise<AxiosResponse<T>>;
  controller: AbortController;
  meta: Record<string, unknown>;
  scope: string | undefined;
};

export type InFlightGetOptions = {
  /**
   * Isolates this call's dedup/supersede pool from other callers of the same
   * getter. Calls with no scope never supersede or get superseded by anything
   * — the safe default for one-off lookups or intentionally-parallel batches
   * (e.g. resolving N recent phone numbers at once). Calls that share a scope
   * compete "latest wins" against each other, and only each other.
   */
  scope?: string;
  /**
   * Skip sharing an in-flight same-key promise and start a new GET instead.
   * The previous same-key request is aborted. The new request is still
   * registered so later duplicates coalesce onto it.
   */
  force?: boolean;
};

/**
 * Dedupes identical in-flight GETs and aborts superseded ones.
 * Same (scope, key) → share promise. Matching supersedePredicate within the
 * same scope → abort previous. Unscoped calls are always left alone.
 * force: true skips same-key reuse, aborts that entry, and starts a new GET.
 */
export function createInFlightGet<TParams extends object, TData>(config: {
  path: string;
  keyOf: (params: TParams) => string;
  /** When starting a request, abort other same-scope in-flight entries this returns true for. */
  shouldSupersede?: (incoming: TParams, inFlightMeta: Record<string, unknown>) => boolean;
  toMeta?: (params: TParams) => Record<string, unknown>;
}) {
  const inFlight = new Map<string, InFlightEntry<TData>>();

  return (params: TParams, options?: InFlightGetOptions): Promise<AxiosResponse<TData>> => {
    const scope = options?.scope;
    const key = `${scope ?? ''}::${config.keyOf(params)}`;
    const existing = inFlight.get(key);
    if (existing) {
      if (!options?.force) {
        return existing.promise;
      }
      existing.controller.abort();
      inFlight.delete(key);
    }

    if (config.shouldSupersede && scope !== undefined) {
      for (const [otherKey, entry] of [...inFlight.entries()]) {
        if (otherKey === key || entry.scope !== scope) {
          continue;
        }
        if (config.shouldSupersede(params, entry.meta)) {
          entry.controller.abort();
          inFlight.delete(otherKey);
        }
      }
    }

    const controller = new AbortController();
    const promise = apiClient
      .get<TData>(config.path, { params, signal: controller.signal })
      .finally(() => {
        if (inFlight.get(key)?.promise === promise) {
          inFlight.delete(key);
        }
      });

    const meta: Record<string, unknown> = config.toMeta
      ? config.toMeta(params)
      : (params as Record<string, unknown>);

    inFlight.set(key, {
      promise,
      controller,
      meta,
      scope,
    });

    return promise;
  };
}

export function stableParamsKey(params: object): string {
  const record = params as Record<string, unknown>;
  const sortedKeys = Object.keys(record).sort();
  const normalized: Record<string, unknown> = {};
  for (const key of sortedKeys) {
    const value = record[key];
    if (value === undefined) {
      continue;
    }
    normalized[key] = value;
  }
  return JSON.stringify(normalized);
}
